import { useCallback, useEffect, useRef } from 'react';
import { millisToSec, secToMillis } from 'utils/time';
import { useAudioPlayer } from '../AudioPlayerContext';
import { usePlaybackState, useTimeupdate } from '../hooks';
import { useTranscriptTimeRangeContext } from '../TranscriptTimeRangeContext';

// since we're not using webaudio, playback is not exact. the epsilon value will
// provide a buffer so that we don't stop playback too later
const STOP_PLAYBACK_EPSILON_MILLIS = 100;

export default function useBindAudioToTimeRange() {
  const { ready } = usePlaybackState();
  const { player } = useAudioPlayer();
  const { transcriptTimeRange } = useTranscriptTimeRangeContext();

  const initialized = useRef(false);

  const startMillis = transcriptTimeRange?.startMillis;
  const endMillis = transcriptTimeRange?.endMillis ?? Infinity;

  const isEnded = useCallback(
    (currentTimeMillis: number) =>
      currentTimeMillis + STOP_PLAYBACK_EPSILON_MILLIS >= endMillis,
    [endMillis],
  );

  useEffect(() => {
    if (ready && !initialized.current) {
      initialized.current = true;

      if (startMillis !== undefined) {
        player.seek(millisToSec(startMillis));
      }
    }
  }, [player, ready, startMillis]);

  useEffect(() => {
    const handlePlay = () => {
      if (isEnded(secToMillis(player.currentTime))) {
        player.seek(millisToSec(startMillis ?? 0));
      }
    };

    player.on('play', handlePlay);

    return () => {
      player.off('play', handlePlay);
    };
  }, [isEnded, player, startMillis]);

  useTimeupdate({
    onTimeupdate: useCallback(
      (sec: number) => {
        if (isEnded(secToMillis(sec))) {
          player.pause();
        }
      },
      [isEnded, player],
    ),
  });
}
